import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from 'axios';
import { ApiConfig } from "@/config/api.config";
import { ApiHookProps } from '@/@types/api.types';
import EToast from '@/components/everridge/EToast';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { useTranslation } from 'react-i18next';
import { CreateUserRequestDto, SetUserPasswordRequestDto, UpdateUserRequestDto, UserDetailsResponseDto } from '@/features/superAdmin/features/users/types/superAdmin-users-dto';
import { LoginResponseDto } from '@/@types/auth.types';

type Props = {} & ApiHookProps;

const getApiErrorDescription = (error: unknown, fallback: string): string => {
    if (!isAxiosError(error) || !error.response?.data) return fallback;

    const data = error.response.data as Record<string, unknown>;
    if (typeof data.detail === 'string' && data.detail.length > 0) return data.detail;

    if (data.errors && typeof data.errors === 'object') {
        const errors = data.errors as Record<string, string[]>;
        const firstMessage = Object.values(errors).flat().find(Boolean);
        if (firstMessage) return firstMessage;
    }

    return fallback;
};

const getApiErrorCode = (error: unknown): string | undefined => {
    if (!isAxiosError(error) || !error.response?.data) return undefined;

    const data = error.response.data as Record<string, unknown>;
    return typeof data.code === 'string' ? data.code : undefined;
};

export const useUserDetailsApi = ({ id }: Props) => {
    const axiosInstance = useAxiosAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();    

    const {
        data: userDetailsData, 
        isLoading: isLoadingUserDetailsData, 
        isError: isErrorUserDetailsData,       
    } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.users.queryKeys.details, 
            id
        ],
        queryFn: async (): Promise<UserDetailsResponseDto> => {
            const url = ApiConfig.queries.superAdmin.users.endpoints.get.replace("{id}", id || '');
            const response = await axiosInstance.get<UserDetailsResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
        enabled: !!id,
    });   

    const {
        mutateAsync: createUser, 
        isPending: isCreatingUser, 
        isError: isErrorCreatingUser
    } = useMutation({
        mutationFn: async (dto: CreateUserRequestDto) => {
            const url = ApiConfig.queries.superAdmin.users.endpoints.create;
            const response = await axiosInstance.post<UserDetailsResponseDto>(url, dto);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ ApiConfig.queries.superAdmin.users.queryKeys.list ]
            });
            EToast({
                variant: 'success',
                message: t('main.superAdmin.users.toasts.create.success.message'), 
                description: t('main.superAdmin.users.toasts.create.success.description')
            });  
        },
        onError: (error) => {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.users.toasts.create.error.message'), 
                description: getApiErrorDescription(
                    error,
                    t('main.superAdmin.users.toasts.create.error.description'),
                ),
            });
        }
    });
    
    const {
        mutateAsync: updateUser, 
        isPending: isUpdatingUser, 
        isError: isErrorUpdatingUser
    } = useMutation({
        mutationFn: async (dto: UpdateUserRequestDto) => {
            const url = ApiConfig.queries.superAdmin.users.endpoints.update.replace("{id}", id || '');
            const response = await axiosInstance.put<UpdateUserRequestDto>(url, dto);
            return response.data;
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ 
                queryKey: [ ApiConfig.queries.superAdmin.users.queryKeys.list ]
            });
            EToast({
                variant: 'success',
                message: t('main.superAdmin.users.toasts.update.success.message'), 
                description: t('main.superAdmin.users.toasts.update.success.description')
            });  
        },
        onError: (error) => {
            const errorCode = getApiErrorCode(error);
            const isLastSuperAdmin = errorCode === 'Users.LastSuperAdmin';

            EToast({
                variant: 'error',
                message: isLastSuperAdmin
                    ? t('main.superAdmin.users.toasts.update.lastSuperAdmin.message')
                    : t('main.superAdmin.users.toasts.update.error.message'),
                description: isLastSuperAdmin
                    ? t('main.superAdmin.users.toasts.update.lastSuperAdmin.description')
                    : getApiErrorDescription(
                          error,
                          t('main.superAdmin.users.toasts.update.error.description'),
                      ),
            });
        }
    });

    const {
        mutateAsync: impersonateUser,
        isPending: isImpersonatingUser,
    } = useMutation({
        mutationFn: async (): Promise<LoginResponseDto> => {
            const url = ApiConfig.queries.superAdmin.users.endpoints.impersonate.replace('{id}', id || '');
            const response = await axiosInstance.post<LoginResponseDto>(url);
            return response.data;
        },
        onError: (error) => {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.users.toasts.impersonate.error.message'),
                description: getApiErrorDescription(
                    error,
                    t('main.superAdmin.users.toasts.impersonate.error.description'),
                ),
            });
        },
    });

    const {
        mutateAsync: setUserPassword,
        isPending: isSettingUserPassword,
    } = useMutation({
        mutationFn: async (dto: SetUserPasswordRequestDto) => {
            const url = ApiConfig.queries.superAdmin.users.endpoints.setPassword.replace('{id}', id || '');
            await axiosInstance.post(url, dto);
        },
        onSuccess: () => {
            EToast({
                variant: 'success',
                message: t('main.superAdmin.users.toasts.setPassword.success.message'),
                description: t('main.superAdmin.users.toasts.setPassword.success.description'),
            });
        },
        onError: (error) => {
            EToast({
                variant: 'error',
                message: t('main.superAdmin.users.toasts.setPassword.error.message'),
                description: getApiErrorDescription(
                    error,
                    t('main.superAdmin.users.toasts.setPassword.error.description'),
                ),
            });
        },
    });

    const {
        mutateAsync: sendPasswordRecoveryEmail,
        isPending: isSendingPasswordRecoveryEmail,
    } = useMutation({
        mutationFn: async () => {
            const url = ApiConfig.queries.superAdmin.users.endpoints.sendPasswordRecoveryEmail.replace(
                '{id}',
                id || '',
            );
            await axiosInstance.post(url);
        },
        onSuccess: () => {
            EToast({
                variant: 'success',
                message: t('main.superAdmin.users.toasts.sendPasswordRecovery.success.message'),
                description: t('main.superAdmin.users.toasts.sendPasswordRecovery.success.description'),
            });
        },
        onError: (error) => {
            const errorCode = getApiErrorCode(error);
            let description = getApiErrorDescription(
                error,
                t('main.superAdmin.users.toasts.sendPasswordRecovery.error.description'),
            );

            if (errorCode === 'Users.EmailNotConfirmed') {
                description = t('main.superAdmin.users.toasts.sendPasswordRecovery.emailNotConfirmed');
            } else if (errorCode === 'Users.UserLockedOut') {
                description = t('main.superAdmin.users.toasts.sendPasswordRecovery.userLockedOut');
            } else if (errorCode === 'Users.EmailNotSet') {
                description = t('main.superAdmin.users.toasts.sendPasswordRecovery.emailNotSet');
            }

            EToast({
                variant: 'error',
                message: t('main.superAdmin.users.toasts.sendPasswordRecovery.error.message'),
                description,
            });
        },
    });

    return {
        userDetailsData,
        isLoadingUserDetailsData,
        isErrorUserDetailsData,
        createUser, 
        isCreatingUser, 
        isErrorCreatingUser,
        updateUser, 
        isUpdatingUser, 
        isErrorUpdatingUser,
        impersonateUser,
        isImpersonatingUser,
        setUserPassword,
        isSettingUserPassword,
        sendPasswordRecoveryEmail,
        isSendingPasswordRecoveryEmail,
    }
};
